import type { ChecklistMessage } from '../module'
import type { deliveryChecklistEmptyState } from '../selectors'
import {
  ChecklistItemRow,
  checklistDragProps,
  ChecklistVisibilityButton,
  ChecklistItemActions,
  ChecklistListActions,
  ChecklistEmptyState
} from '~/features/checklists/list'
import type { checklistRows } from '../selectors'
import type { STChecklistItem } from '@st/sdk'
import type { DragHandlersProps, DragState } from '@st/react-util/use-draggable'
import { Checklist, Button, LockClosedIcon } from '@st/theme'

export function DeliveryChecklist({
  send,
  rows,
  onSelectDocument,
  isArchived,
  emptyState,
  clientName,
  dragHandlers,
  dragState
}: {
  send: (message: ChecklistMessage) => void
  rows: ReturnType<typeof checklistRows>
  onSelectDocument: (documentId: string) => void
  isArchived: boolean
  emptyState: ReturnType<typeof deliveryChecklistEmptyState>
  clientName: string
  dragHandlers: (item: STChecklistItem) => DragHandlersProps
  dragState: DragState<STChecklistItem>
}) {
  const addItem = (
    <Button
      variant="default"
      disabled={isArchived}
      onClick={() =>
        send({ type: 'openChecklistDialog', request: { mode: 'create', phase: 'delivery' } })
      }
    >
      Add item
    </Button>
  )
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <h1 className="min-w-0 flex-1 text-3xl">Tax payment checklist</h1>
        {rows.length > 0 && (
          <ChecklistListActions send={send} phase="delivery" disabled={isArchived} />
        )}
      </div>
      {rows.length === 0 && emptyState.kind == 'locked' && (
        <div className="flex flex-col items-center px-6 py-12 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-gray-400">
            <LockClosedIcon className="size-6 text-white" />
          </div>
          <p className="mt-4 max-w-md text-sm text-gray-600">
            This checklist will activate once you deliver a tax return to {clientName}. To deliver a
            tax return, simply drag it into the binder.
          </p>
          <Button variant="primary" className="mt-4" disabled>
            Auto-generate checklist
          </Button>
        </div>
      )}
      {rows.length === 0 && emptyState.kind == 'empty' && (
        <ChecklistEmptyState instructions={emptyState.instructions}>
          <div className="flex items-center gap-2">
            {emptyState.canAutoGenerate && (
              <Button
                variant="primary"
                disabled={isArchived}
                onClick={() => send({ type: 'previewDeliveryChecklist' })}
              >
                Auto-generate checklist
              </Button>
            )}
            {addItem}
          </div>
        </ChecklistEmptyState>
      )}
      {rows.length > 0 && (
        <Checklist.Table>
          {rows.map(({ item, documents }) => (
            <ChecklistItemRow
              key={item.id}
              item={item}
              documents={documents}
              onSelectDocument={onSelectDocument}
              {...checklistDragProps({ item, isArchived, dragHandlers, dragState })}
              checkbox={
                <Checklist.CheckboxCell
                  disabled={isArchived}
                  checked={item.status === 'complete'}
                  onChange={() =>
                    send({
                      type: 'openChecklistDialog',
                      request: { mode: 'manageCompletion', item }
                    })
                  }
                />
              }
              actions={
                <>
                  <ChecklistVisibilityButton item={item} disabled={isArchived} send={send} />
                  <ChecklistItemActions
                    name={item.name}
                    disabled={isArchived}
                    onAction={(mode) =>
                      send({ type: 'openChecklistDialog', request: { mode, item } })
                    }
                  />
                </>
              }
            />
          ))}
        </Checklist.Table>
      )}
    </div>
  )
}
